/**
 * Content Carousel Scroll Animation
 * Pins the carousel container and changes slides based on scroll progress using GSAP ScrollTrigger
 */

(function() {
    'use strict';

    let resizeTimer = null;
    let resizeCallbacks = [];
    let initAttempts = 0;
    const MAX_INIT_ATTEMPTS = 50;
    const initializedCarousels = new WeakSet();
    const MOBILE_BREAKPOINT = 901;

    function isMobile() {
        return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
    }

    function initContentCarousel() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
            initAttempts++;
            if (initAttempts < MAX_INIT_ATTEMPTS) {
                setTimeout(initContentCarousel, 100);
            }
            return;
        }

        try {
            gsap.registerPlugin(ScrollTrigger);
        } catch (e) {
            return;
        }

        const carousels = document.querySelectorAll('.wp-block-bmg-content-carousel');
        
        if (carousels.length === 0) {
            return;
        }

        carousels.forEach((carousel, carouselIndex) => {
            if (initializedCarousels.has(carousel)) {
                return;
            }
            initializedCarousels.add(carousel);

            const slides = carousel.querySelectorAll('.slide');
            const bullets = carousel.querySelectorAll('.bullet');
            const slideCount = slides.length;

            if (slideCount <= 1) {
                return;
            }

            let vh = window.innerHeight || document.documentElement.clientHeight;
            let slideDistance = vh;
            let totalScrollDistance = slideCount * slideDistance;
            let pinTrigger = null;

            function recalculateDimensions() {
                vh = window.innerHeight || document.documentElement.clientHeight;
                slideDistance = vh;
                totalScrollDistance = slideCount * slideDistance;
            }

            function waitForImages(callback) {
                const images = carousel.querySelectorAll('img');
                if (images.length === 0) {
                    callback();
                    return;
                }

                let loadedCount = 0;
                const totalImages = images.length;
                let allComplete = false;

                function checkComplete() {
                    if (allComplete) return;
                    loadedCount++;
                    if (loadedCount === totalImages) {
                        allComplete = true;
                        callback();
                    }
                }

                images.forEach((img) => {
                    if (img.complete) {
                        checkComplete();
                    } else {
                        img.addEventListener('load', checkComplete, { once: true });
                        img.addEventListener('error', checkComplete, { once: true });
                    }
                });
            }

            waitForImages(() => {
                recalculateDimensions();
                
                // Ne pas créer le pinTrigger sur mobile
                if (!isMobile()) {
                    createOrUpdatePinTrigger();
                    if (typeof ScrollTrigger !== 'undefined') {
                        try {
                            ScrollTrigger.refresh();
                        } catch (e) {
                            // Ignore refresh errors
                        }
                    }
                }
            });

            // Sur mobile, afficher tous les slides sans animation
            if (isMobile()) {
                slides.forEach((slide, index) => {
                    gsap.set(slide, { opacity: 1, visibility: 'visible' });
                    const image = slide.querySelector('.image');
                    if (image) {
                        gsap.set(image, { clipPath: 'inset(0% 0% 0% 0% round 14px)' });
                    }
                });
                // Activer tous les bullets sur mobile
                bullets.forEach(bullet => bullet.classList.add('active'));
            } else {
                // Comportement desktop : animation normale
                slides.forEach((slide, index) => {
                    const image = slide.querySelector('.image');
                    if (index === 0) {
                        gsap.set(slide, { opacity: 1, visibility: 'visible' });
                        if (image) {
                            gsap.set(image, { clipPath: 'inset(0% 100% 0% 0% round 14px)' });
                            gsap.to(image, {
                                clipPath: 'inset(0% 0% 0% 0% round 14px)',
                                duration: 0.6,
                                ease: 'power1.out',
                            });
                        }
                    } else {
                        gsap.set(slide, { opacity: 0, visibility: 'hidden' });
                        if (image) {
                            gsap.set(image, { clipPath: 'inset(0% 100% 0% 0% round 14px)' });
                        }
                    }
                });
                if (bullets[0]) {
                    bullets[0].classList.add('active');
                }
            }

            let currentActiveIndex = 0;
            let lastProgress = -1;
            let lastUpdateTime = 0;
            let isTransitioning = false;
            let isProgrammaticScroll = false; // Flag pour ignorer onUpdate pendant un scroll programmatique
            let currentAnimations = [];
            const MIN_UPDATE_INTERVAL = 200; // Augmenté pour filtrer les événements trop fréquents de Lenis
            const PROGRESS_THRESHOLD = 0.05; // Seuil de changement de progress (5% au lieu de 1%)
            const TRANSITION_DURATION = 0.5;

            function changeSlide(index) {
                if (index < 0 || index >= slideCount || currentActiveIndex === index) {
                    return;
                }
                
                const previousIndex = currentActiveIndex;
                const previousSlide = slides[previousIndex];
                const nextSlide = slides[index];
                
                if (!nextSlide) {
                    return;
                }
                
                if (isTransitioning && currentAnimations.length > 0) {
                    currentAnimations.forEach(anim => {
                        if (anim && anim.kill) {
                            anim.kill();
                        }
                    });
                    currentAnimations = [];
                    
                    slides.forEach((slide, idx) => {
                        if (idx !== index) {
                            const image = slide.querySelector('.image');
                            gsap.set(slide, { opacity: 0, visibility: 'hidden' });
                            if (image) {
                                gsap.set(image, { clipPath: 'inset(0% 100% 0% 0% round 14px)' });
                            }
                        }
                    });
                }
                
                currentActiveIndex = index;
                isTransitioning = true;
                
                bullets.forEach((bullet, idx) => {
                    if (idx === index) {
                        bullet.classList.add('active');
                    } else {
                        bullet.classList.remove('active');
                    }
                });
                
                const previousImage = previousSlide?.querySelector('.image');
                const nextImage = nextSlide.querySelector('.image');
                
                function showNextSlide() {
                    gsap.set(nextSlide, { opacity: 1, visibility: 'visible' });
                    
                    if (nextImage) {
                        gsap.set(nextImage, { clipPath: 'inset(0% 100% 0% 0% round 14px)' });
                        
                        const clipInAnim = gsap.to(nextImage, {
                            clipPath: 'inset(0% 0% 0% 0% round 14px)',
                            duration: 0.6,
                            ease: 'power1.out',
                            onComplete: () => {
                                isTransitioning = false;
                                currentAnimations = [];
                            }
                        });
                        currentAnimations.push(clipInAnim);
                    } else {
                        isTransitioning = false;
                        currentAnimations = [];
                    }
                }
                
                gsap.set(nextSlide, { opacity: 0, visibility: 'hidden' });
                
                if (previousSlide && previousIndex !== index) {
                    if (previousImage) {
                        const clipOutAnim = gsap.to(previousImage, {
                            clipPath: 'inset(0% 100% 0% 0% round 14px)',
                            duration: 0.6,
                            ease: 'power1.out',
                            onComplete: () => {
                                gsap.set(previousSlide, { opacity: 0, visibility: 'hidden' });
                                showNextSlide();
                            }
                        });
                        currentAnimations.push(clipOutAnim);
                    } else {
                        const fadeOutAnim = gsap.to(previousSlide, {
                            opacity: 0,
                            visibility: 'hidden',
                            duration: TRANSITION_DURATION * 0.6,
                            ease: 'power1.out',
                            onComplete: showNextSlide
                        });
                        currentAnimations.push(fadeOutAnim);
                    }
                } else {
                    showNextSlide();
                }
            }

            function createOrUpdatePinTrigger() {
                if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
                    return;
                }

                // Ne pas créer le pinTrigger sur mobile
                if (isMobile()) {
                    if (pinTrigger?.scrollTrigger) {
                        pinTrigger.scrollTrigger.kill();
                        pinTrigger = null;
                    }
                    return;
                }

                try {
                    if (pinTrigger?.scrollTrigger) {
                        pinTrigger.scrollTrigger.kill();
                    }

                    const maxSlideIndex = slideCount - 1;

                    pinTrigger = gsap.to(carousel, {
                        scrollTrigger: {
                            trigger: carousel,
                            pin: carousel,
                            start: 'top top',
                            end: `+=${totalScrollDistance}`,
                            pinSpacing: true,
                        onUpdate: (self) => {
                            const progress = self.progress;
                            const isActive = self.isActive;
                            
                            if (!isActive) {
                                return;
                            }
                            
                            // Ignorer les changements si une transition est en cours
                            if (isTransitioning) {
                                return;
                            }
                            
                            // Ignorer les changements si un scroll programmatique est en cours
                            if (isProgrammaticScroll) {
                                return;
                            }
                            
                            const now = Date.now();
                            const timeSinceLastUpdate = now - lastUpdateTime;
                            const progressChanged = lastProgress === -1 || Math.abs(progress - lastProgress) > PROGRESS_THRESHOLD;
                            
                            if (progressChanged && timeSinceLastUpdate >= MIN_UPDATE_INTERVAL) {
                                const slideIndex = Math.min(Math.floor(progress * slideCount), maxSlideIndex);
                                
                                if (slideIndex !== currentActiveIndex) {
                                    changeSlide(slideIndex);
                                    lastProgress = progress;
                                    lastUpdateTime = now;
                                } else {
                                    lastProgress = progress;
                                }
                            }
                        }
                    }
                });
                } catch (e) {
                    // Ignore creation errors
                }
            }

            bullets.forEach((bullet, index) => {
                bullet.addEventListener('click', () => {
                    // Ne pas gérer les clics sur mobile
                    if (isMobile()) {
                        return;
                    }
                    
                    if (typeof ScrollTrigger === 'undefined') {
                        return;
                    }
                    
                    // Recalculer les dimensions avant le scroll pour s'assurer qu'elles sont à jour
                    recalculateDimensions();
                    
                    const pinTriggerInstance = pinTrigger?.scrollTrigger;
                    if (pinTriggerInstance) {
                        // Recalculer le start au moment du clic pour avoir la valeur la plus récente
                        const currentStart = pinTriggerInstance.start;
                        const targetProgress = index / slideCount;
                        const targetScroll = currentStart + (targetProgress * totalScrollDistance);
                        
                        // Activer le flag pour ignorer les onUpdate pendant le scroll programmatique
                        isProgrammaticScroll = true;
                        
                        // Changer directement la slide sans attendre le scroll
                        if (index !== currentActiveIndex) {
                            changeSlide(index);
                        }
                        
                        // Essayer d'utiliser Lenis directement s'il est accessible
                        if (typeof window !== 'undefined' && window.lenis) {
                            window.lenis.scrollTo(targetScroll, {
                                duration: 1.2,
                                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                                immediate: false
                            });
                            
                            // Attendre que le scroll soit terminé avant de réactiver les onUpdate
                            setTimeout(() => {
                                isProgrammaticScroll = false;
                                if (typeof ScrollTrigger !== 'undefined') {
                                    ScrollTrigger.refresh();
                                }
                            }, 1300);
                        } else {
                            // Fallback : utiliser window.scrollTo() - Lenis devrait gérer le smooth scroll
                            window.scrollTo({
                                top: targetScroll,
                                behavior: 'smooth'
                            });
                            
                            // Attendre que le scroll soit terminé
                            setTimeout(() => {
                                isProgrammaticScroll = false;
                                if (typeof ScrollTrigger !== 'undefined') {
                                    ScrollTrigger.refresh();
                                }
                            }, 1000);
                        }
                    }
                });
            });

            const resizeCallback = () => {
                if (typeof ScrollTrigger === 'undefined') {
                    return;
                }
                
                const wasMobile = typeof resizeCallback.lastWasMobile !== 'undefined' ? resizeCallback.lastWasMobile : !isMobile();
                const nowMobile = isMobile();
                
                recalculateDimensions();
                
                if (nowMobile) {
                    // Mode mobile : détruire le pinTrigger et afficher tous les slides
                    if (pinTrigger?.scrollTrigger) {
                        pinTrigger.scrollTrigger.kill();
                        pinTrigger = null;
                    }
                    
                    // Afficher tous les slides sans animation
                    slides.forEach((slide) => {
                        gsap.set(slide, { opacity: 1, visibility: 'visible' });
                        const image = slide.querySelector('.image');
                        if (image) {
                            gsap.set(image, { clipPath: 'inset(0% 0% 0% 0% round 14px)' });
                        }
                    });
                    
                    // Activer tous les bullets
                    bullets.forEach(bullet => bullet.classList.add('active'));
                } else {
                    // Mode desktop : recréer le pinTrigger si nécessaire
                    if (wasMobile || !pinTrigger?.scrollTrigger) {
                        // Réinitialiser les slides pour le mode desktop
                        slides.forEach((slide, index) => {
                            const image = slide.querySelector('.image');
                            if (index === 0) {
                                gsap.set(slide, { opacity: 1, visibility: 'visible' });
                                if (image) {
                                    gsap.set(image, { clipPath: 'inset(0% 0% 0% 0% round 14px)' });
                                }
                            } else {
                                gsap.set(slide, { opacity: 0, visibility: 'hidden' });
                                if (image) {
                                    gsap.set(image, { clipPath: 'inset(0% 100% 0% 0% round 14px)' });
                                }
                            }
                        });
                        
                        // Réinitialiser les bullets
                        bullets.forEach((bullet, idx) => {
                            if (idx === 0) {
                                bullet.classList.add('active');
                            } else {
                                bullet.classList.remove('active');
                            }
                        });
                        
                        currentActiveIndex = 0;
                    }
                    
                    try {
                        ScrollTrigger.refresh();
                    } catch (e) {
                        // Ignore refresh errors
                    }
                    createOrUpdatePinTrigger();
                }
                
                resizeCallback.lastWasMobile = nowMobile;
            };
            
            resizeCallbacks.push(resizeCallback);
            
            if (resizeCallbacks.length === 1) {
                window.addEventListener('resize', () => {
                    clearTimeout(resizeTimer);
                    resizeTimer = setTimeout(() => {
                        resizeCallbacks.forEach(callback => callback());
                    }, 250);
                }, { passive: true });
            }
        });
    }

    function safeInit() {
        if (typeof document === 'undefined') {
            return;
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initContentCarousel);
        } else {
            if (document.body) {
                initContentCarousel();
            } else {
                setTimeout(safeInit, 50);
            }
        }
    }

    safeInit();
})();
